/**
 * OWNER: W0 (orchestrator; change request CR-W2-05-4). React bindings over `AppServices` shared by every feature.
 *
 * App-level dialogs, dock panels and status items read the open project through `services.store` (it follows
 * project open / close / swap) and the editor store, the settings and the problems through `services` (fakes in
 * tests), which `state/react.ts` (`useDoc`, context-based) and `state/editorStore.ts` (`useEditor`, the global
 * store) do not cover. Features import this module (eslint `features` zone) instead of keeping private copies.
 * Translation helpers stay per feature (`useT` / `services.i18n` wrappers).
 *
 * W3-01 (UX-006): `lazyComponent` lets a module register a heavy panel, page or dialog that loads on first use (its
 * code and CSS then live in their own chunk), as a plain function component with a `preload()` to warm it.
 */

import {
  createElement,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ComponentType,
  type ReactNode,
} from 'react';
import type { ChartDoc, DocIndexes } from '../../core/model/types';
import type { Unsubscribe } from '../../core/util/types';
import { useExternalSelector } from '../../state/react';
import type { DocStore, EditorStore, VersionTopic } from '../../state/types';
import type { UserSettings } from '../settings/types';
import type { AppServices } from './types';

/** The open project's DocStore (null in the Library), following project open / close. */
export function useServicesStore(services: Pick<AppServices, 'store' | 'onChange'>): DocStore | null {
  const subscribe = useCallback((cb: () => void): Unsubscribe => services.onChange(cb), [services]);
  const get = useCallback((): DocStore | null => services.store, [services]);
  return useSyncExternalStore(subscribe, get, get);
}

/** A counter bumped by every document change of the store (committed transactions and live gestures). */
export function useDocVersion(store: DocStore | null): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (store === null) return undefined;
    return store.subscribe(() => {
      setVersion((v) => v + 1);
    });
  }, [store]);
  return version;
}

/**
 * `selector(doc, idx)` of an explicit store, re-run only when one of `topics` changed (or the selector changed;
 * ARCHITECTURE §5.6). Records are immutable, so selecting records compares by identity; aggregated results (fresh
 * arrays of a table's records) pass an `equal`.
 */
export function useDocValue<T>(
  store: DocStore,
  selector: (doc: ChartDoc, idx: DocIndexes) => T,
  topics: readonly VersionTopic[],
  equal?: (a: T, b: T) => boolean,
): T {
  const key = topics.join('\u0000');
  const stable = useMemo(() => (key === '' ? [] : (key.split('\u0000') as VersionTopic[])), [key]);
  const subscribe = useCallback((cb: () => void): Unsubscribe => store.subscribeVersions(stable, cb), [store, stable]);
  const getStamp = useCallback((): number => store.stamp(stable), [store, stable]);
  const select = useCallback((): T => selector(store.doc, store.idx), [store, selector]);
  // Keyed on the store AND the topics: two topic lists can have equal stamps.
  const source = useMemo(() => ({ store, topics: stable }), [store, stable]);
  return useExternalSelector(source, subscribe, getStamp, select, equal);
}

/** The user settings (re-renders on every settings change). */
export function useSettings(services: Pick<AppServices, 'settings'>): UserSettings {
  const { settings } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => settings.subscribe(cb), [settings]);
  const get = useCallback((): UserSettings => settings.value, [settings]);
  return useSyncExternalStore(subscribe, get, get);
}

/**
 * A value of the editor store (selection, level, snap, active line...); `selector` may derive values when `equal`
 * says when two results are the same.
 */
export function useEditorValue<T>(
  services: Pick<AppServices, 'editor'>,
  selector: (s: EditorStore) => T,
  equal?: (a: T, b: T) => boolean,
): T {
  const { editor } = services;
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe =>
      editor.subscribe(() => {
        cb();
      }),
    [editor],
  );
  const get = useCallback((): EditorStore => editor.getState(), [editor]);
  return useExternalSelector(editor, subscribe, get, selector, equal);
}

/** A component that loads on first render (see `lazyComponent`). */
export interface LazyComponent<P> {
  (props: P): ReactNode;
  /** Starts loading now (e.g. when the app is idle), so the first render does not wait. */
  preload(): Promise<void>;
}

/**
 * A component whose code loads on first render: `lazyComponent(() => import('./TimingWorkspace').then((m) => ({
 * default: m.TimingWorkspace })))`. Usable wherever a module gives a component (panels, routes, dialogs, status
 * items). While loading it renders `fallback` (default: nothing); a failed load throws into the nearest error
 * boundary (the shell's panel and page boundaries say so and offer a reload).
 */
export function lazyComponent<P extends object>(
  load: () => Promise<{ readonly default: ComponentType<P> }>,
  fallback: ReactNode = null,
): LazyComponent<P> {
  let pending: Promise<{ readonly default: ComponentType<P> }> | null = null;
  const once = (): Promise<{ readonly default: ComponentType<P> }> => {
    pending ??= load().catch((err: unknown) => {
      pending = null;
      throw err;
    });
    return pending;
  };
  const Lazy = lazy(once);
  const Loaded = (props: P): ReactNode =>
    createElement(Suspense, { fallback }, createElement(Lazy as unknown as ComponentType<P>, props));
  return Object.assign(Loaded, {
    preload: () => once().then(() => undefined),
  });
}
